import { ReactNode, useContext, useEffect, useState } from "react";
import {
  PlacementEnvironmentDetailChecklistStage,
  PlacementEnvironmentDetailChecklistStageTaskPeople,
} from "../../api/types/placement-environment";
import { Avatar } from "primereact/avatar";
import { AvatarGroup } from "primereact/avatargroup";
import { useRepresentatives } from "../../api/queries/representatives/useRepresentatives";
import { useUsers } from "../../api/queries/user/useUsers";
import { none } from "../common";
import { userContext } from "../../context/user";

export const getStagePercents = (
  stage: PlacementEnvironmentDetailChecklistStage,
): string => {
  const taskTotal = stage.checklist_tasks_user.length;

  const taskFinished = stage.checklist_tasks_user.filter(
    (task) => task.completed === true,
  ).length;

  return ((taskFinished * 100) / taskTotal).toFixed(0);
};

export const usePersonOptions = () => {
  const { data: representativesResp } = useRepresentatives({
    offset: 0,
    limit: 100,
  });
  const { isAdmin, detail } = useContext(userContext);
  const { data: usersResp } = useUsers({ offset: 0, limit: 100 }, isAdmin());
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);

  useEffect(() => {
    const representatives =
      representativesResp?.data.results.map((representative) => ({
        id: representative.id,
        label: `${representative.last_name} ${representative.first_name}`,
        type: "representative" as PersonOptionType,
      })) || [];

    const users =
      usersResp?.data.results.map((user) => ({
        id: user.id,
        label: user.email,
        type: "user" as PersonOptionType,
      })) || [];

    if (!isAdmin() && detail) {
      users.push({
        id: detail.id,
        label: detail.email,
        type: "user",
      });
    }

    setPersonOptions([...representatives, ...users]);
  }, [representativesResp, usersResp, detail, isAdmin]);

  return personOptions;
};

export const getResponsibleAvatars = (
  options: PersonOption[],
  people: PlacementEnvironmentDetailChecklistStageTaskPeople[],
): ReactNode => {
  let avatarOptions: PersonOption[] = [];

  people.forEach((person) => {
    let item: PersonOption | undefined;

    if (person.user) {
      item = options.find(
        (option) =>
          option.id === person.user?.id && person.person_type === "responsible",
      );
    }

    if (person.representative) {
      item = options.find(
        (option) =>
          option.id === person.representative?.id &&
          person.person_type === "responsible",
      );
    }

    if (item) {
      avatarOptions.push(item);
    }
  });

  if (avatarOptions.length === 0) {
    return none;
  }

  return (
    <AvatarGroup>
      {avatarOptions.map((option) => {
        let initials: string = "";
        const splits = option.label.split(" ");

        if (splits.length === 1) {
          initials = splits[0].charAt(0);
        } else if (splits.length === 2) {
          initials = `${(initials = splits[0].charAt(0))}${(initials =
            splits[1].charAt(0))}`;
        }

        return (
          <Avatar
            key={option.id}
            label={initials.toUpperCase()}
            size="normal"
            shape="circle"
            title={option.label}
            style={{ backgroundColor: "#9c27b0", color: "#ffffff" }}
          />
        );
      })}
    </AvatarGroup>
  );
};

export type PersonOptionType = "representative" | "user";

export interface PersonOption {
  id: string;
  label: string;
  type: PersonOptionType;
}
